import { createSlice } from '@reduxjs/toolkit';

import api from 'services/api';
import { executeAiTopupRecaptcha } from 'utils/recaptcha';

// The store's AI credits: the summary (balance, settings, card), usage for a
// period, and recent top-ups
export const initialState = {
  summary: null,
  usage: null,
  topups: null,
};

const slice = createSlice({
  name: 'aiCredits',
  initialState,
  reducers: {
    setSummary(state, { payload }) {
      state.summary = payload;
    },
    setUsage(state, { payload }) {
      state.usage = payload;
    },
    setTopups(state, { payload }) {
      state.topups = payload;
    },
    // A top-up just bought shows first, before the list is read again
    setTopup(state, { payload }) {
      const results = (state.topups?.results || []).filter(
        (topup) => topup.id !== payload.id,
      );
      const isNew = results.length === (state.topups?.results || []).length;

      state.topups = {
        ...state.topups,
        count: (state.topups?.count || 0) + (isNew ? 1 : 0),
        results: [payload, ...results],
      };
    },
  },
});

export const { reducer } = slice;
export const { setSummary, setUsage, setTopups, setTopup } = slice.actions;

/**
 * @param {object} topup
 * @returns {boolean} whether its payment is still being made
 */
export function isTopupPending(topup) {
  return topup?.status === 'pending' || topup?.status === 'charging';
}

const aiCreditsActions = {
  fetchSummary() {
    return async (dispatch) => {
      const summary = await api.get('/account/ai-credits');
      dispatch(setSummary(summary));
      return summary;
    };
  },

  fetchUsage({ period, page } = {}) {
    return async (dispatch) => {
      const usage = await api.get('/account/ai-credits/usage', {
        period,
        page,
      });
      dispatch(setUsage(usage));
      return usage;
    };
  },

  fetchTopups() {
    return async (dispatch) => {
      const topups = await api.get('/account/ai-credits/topups');
      dispatch(setTopups(topups));
      return topups;
    };
  },

  /**
   * Buys credits. The request ID stays the same for retries of one purchase,
   * so it is charged once. A refused or failed purchase throws, with a
   * message to show
   * @param {number} amountMicros
   * @param {string} requestId
   * @returns {Function}
   */
  createTopup(amountMicros, requestId) {
    return async (dispatch, getState) => {
      const recaptcha = await executeAiTopupRecaptcha(getState().client);
      const topup = await api.post('/account/ai-credits/topups', {
        amount_micros: amountMicros,
        request_id: requestId,
        recaptcha,
      });
      dispatch(setTopup(topup));
      return topup;
    };
  },

  /**
   * @param {object} data auto_recharge and monthly_limit_micros
   * @returns {Function} resolves to the new summary, or `{ errors }` by field
   */
  updateSettings(data) {
    return async (dispatch) => {
      const result = await api.put('/account/ai-credits/settings', data);
      if (result && !result.errors) {
        dispatch(setSummary(result));
      }
      return result;
    };
  },
};

export default aiCreditsActions;
